'use client';

import { getAppsGatewayUrl } from '../../../../../../../constants/env';
import {
  DiscoLayout,
  WORKFLOW_IDS,
  DISPLAY_MODES,
  VIEWS,
  LAYOUT_IDS,
  COVER_IMAGE_PARTNER_TEST_NAME,
  BANNER_FORMAT_PARTNER_TEST_NAME,
  EDD_DRAWER_PARTNER_TEST_NAME,
  type EddDrawerArm,
} from './constants';
import type { BannerFormatArm } from './bannerFormat';
import type {
  DiscoRecommendation,
  DiscoRecommendationGroupItem,
  DiscoFooter,
  DiscoRecommendationsResponse,
  RunningExperiment,
} from './types';
import type { DiscoRecommendations, InvokeRequest } from './schemas';

export interface DiscoServerData {
  recommendations: Record<string, DiscoRecommendation>;
  group: DiscoRecommendationGroupItem[];
  footer: DiscoFooter;
  vendorSessionId: string;
  orderNumber?: string;
  trackingNumber?: string;
  numAdsReturned: number;
}

interface FetchDiscoRecommendationsParams {
  jwt: string;
  sessionId: string;
  retailerMoniker: string;
  orderNumber?: string;
  trackingNumber?: string;
  carrierMoniker?: string;
  trackingStatus?: string;
  deviceType: 'mobile' | 'desktop';
  layout: DiscoLayout;
  isCoverImage?: boolean;
  bannerFormatArm?: BannerFormatArm;
  eddDrawerArm?: EddDrawerArm;
  sandbox?: boolean;
  locale?: string;
  view?: (typeof VIEWS)[keyof typeof VIEWS];
}

export async function sha256Hex16(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  );
  const hex = Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}

export async function fetchDiscoRecommendations(
  params: FetchDiscoRecommendationsParams,
): Promise<DiscoServerData | null> {
  const {
    jwt,
    sessionId,
    retailerMoniker,
    orderNumber,
    trackingNumber,
    carrierMoniker,
    trackingStatus,
    deviceType,
    layout,
    isCoverImage = false,
    bannerFormatArm,
    eddDrawerArm,
    sandbox = false,
    locale = 'en_US',
    view = VIEWS.ORDER_TRACKING,
  } = params;

  if (!orderNumber && !trackingNumber) return null;

  const language = locale.split(/[_-]/)[0];
  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  const displayMode =
    layout === DiscoLayout.Drawer
      ? DISPLAY_MODES.PULLUP
      : layout === DiscoLayout.Cover
        ? DISPLAY_MODES.OVERLAY
        : DISPLAY_MODES.INLINE;

  const isTablet = userAgent ? /tablet|ipad/i.test(userAgent) : false;
  const deviceTypeMapping =
    deviceType === 'mobile' ? (isTablet ? 'TABLET' : 'PHONE') : 'DESKTOP';
  const platformType = deviceType === 'mobile' ? 'MOBILE' : 'WEB';

  const runningExperiments: RunningExperiment[] = [];
  if (isCoverImage) {
    runningExperiments.push({
      name: COVER_IMAGE_PARTNER_TEST_NAME,
      version: 1,
    });
  }
  if (bannerFormatArm) {
    runningExperiments.push({
      name: BANNER_FORMAT_PARTNER_TEST_NAME,
      version: bannerFormatArm === 'CONTROL' ? 0 : 1,
    });
  }
  if (eddDrawerArm) {
    runningExperiments.push({
      name: EDD_DRAWER_PARTNER_TEST_NAME,
      version: eddDrawerArm === 'CONTROL' ? 0 : 1,
    });
  }

  const discoBody: DiscoRecommendations = {
    placement_details: {
      display_mode: displayMode,
      layout_id: LAYOUT_IDS.APP_NATIVE_ESSENTIAL,
      view,
    },
    custom_metadata: {
      is_sandbox: sandbox,
      ...(trackingStatus && { tracking_status: trackingStatus }),
    },
    attributes: {
      user_agent: userAgent,
      device_type: deviceTypeMapping,
      platform_type: platformType,
      language,
    },
    ...(runningExperiments.length > 0 && {
      running_experiments: runningExperiments,
    }),
  };

  const invokeRequest: InvokeRequest = {
    workflowId: WORKFLOW_IDS.DISCO_RECOMMENDATIONS,
    context: {
      sessionId,
      retailerMoniker,
      orderNumber,
      trackingNumber,
      carrierMoniker,
    },
    body: discoBody,
  };

  try {
    const gatewayUrl = getAppsGatewayUrl();
    const res = await fetch(`${gatewayUrl}/invoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(invokeRequest),
    });

    if (!res.ok) return null;

    const { data } = (await res.json()) as {
      data: DiscoRecommendationsResponse;
    };

    return {
      recommendations: data.recommendations,
      group: data.recommendation_groups.group_0,
      footer: data.footer,
      vendorSessionId: data.session_id,
      orderNumber: data.resolved_order_number ?? orderNumber,
      trackingNumber,
      numAdsReturned: data.recommendation_groups.group_0?.length ?? 0,
    };
  } catch (err) {
    console.error('Failed to fetch Disco recommendations:', err);
    return null;
  }
}
